<script setup lang="ts">
import type { IPhotosItemGalleryDataClientDTO } from '~/shared/api/client/photos/photos.dto';
import SliderModalGallery from '~/shared/ui/slider/SliderModalGallery.vue';
import {
  resizeImage,
  resizeImageSetLayers,
  resizeImageSrcsetWidths,
} from '~/shared/lib/resizeImage';

const props = defineProps<{
  gallery: IPhotosItemGalleryDataClientDTO;
}>();

const isModalOpened = ref(false);
const startIndex = ref(0);

const photos = computed(() => props.gallery?.photos);
const photoOne = computed(() => photos.value?.[0]);

const isMobile = computed(() => useNuxtApp().$viewport.isLessOrEquals('xs'));

function openModal(index: number) {
  startIndex.value = index;
  isModalOpened.value = true;
}
const thumbnailStyle = (url: string) =>
  resizeImageSetLayers(url, { width: 172, height: 96, mode: 'fill' })
    .map((layer) => `background-image: ${layer}`)
    .join('; ');
</script>

<template>
  <div :class="$style.image">
    <div :class="$style.image__wrapper">
      <img
        :src="resizeImage(photoOne?.photo.file_url, { width: 860 })"
        :srcset="
          resizeImageSrcsetWidths(
            photoOne?.photo.file_url,
            [480, 720, 860, 1280, 1720, 2580],
          ) || undefined
        "
        sizes="(max-width: 900px) 100vw, 860px"
        :class="$style.image__img"
        :alt="photoOne?.description"
        @click="openModal(0)"
      />
    </div>
    <div :class="$style.image__gallery">
      <div
        v-for="(item, index) in isMobile
          ? photos?.slice(1)
          : photos?.slice(1, 6)"
        :key="`gallery${gallery.id}_photo${item.id}`"
        :class="$style.image__galleryItem"
        :style="thumbnailStyle(item.photo.file_url)"
        @click="openModal(index + 1)"
      >
        <div
          v-if="
            index === 4 && !isMobile && photos?.length && photos?.length > 6
          "
          :class="$style.image__galleryMore"
        >
          +{{ photos?.length - 6 }}
        </div>
      </div>
    </div>
    <Modal
      v-if="photos?.length && photos.length > 0"
      :is-visible="isModalOpened"
      title="Фотогалерея"
      :class="$style.modal"
      @close="isModalOpened = false"
    >
      <SliderModalGallery
        :photos="photos"
        :start-index="startIndex"
        :is-open="isModalOpened"
        :is-mobile="isMobile"
      />
    </Modal>
  </div>
</template>

<style module lang="scss">
.image {
  display: flex;
  flex-flow: column nowrap;
  gap: 2px;
  padding: 0;
  margin: var(--space-2x) 0;

  &__wrapper {
    position: relative;
    display: flex;
  }

  &__img {
    width: 100%;
    height: auto;
    margin: 0 !important;
    cursor: pointer;
  }

  &__source {
    @include text_body2;
    color: var(--color-gray-text);
  }

  &__gallery {
    display: flex;
    flex-direction: row;
    gap: 2px;

    &Item {
      position: relative;
      width: 20%;
      height: 96px;
      background-size: cover;
      background-position: center;
      cursor: pointer;
    }

    &More {
      position: relative;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      @include text_h4;
      color: var(--color-white);
    }
  }
}

@media (max-width: 479px) {
  .image {
    &__wrapper {
      margin-left: -20px;
      margin-right: -20px;
    }

    &__gallery {
      margin-left: -20px;
      margin-right: -20px;
      @include mobileSliderWrapper;
      &Item {
        @include mobileSliderItem;
        width: 170px;
      }
    }
  }
}
</style>
